<template>
  <!-- Mismo formato que el listado de membresías de un contrato en MRR Breakdown.
       Sin `persistent`: se cierra con Esc o clic afuera. -->
  <v-dialog :value="value" max-width="1100" scrollable @input="$emit('input', $event)">
    <v-card class="pa-4">
      <v-card-title class="text-uppercase pb-0">
        <v-btn v-if="contract" icon small class="mr-2" aria-label="Volver a contratos" @click="$emit('back')">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        {{ heading }}
      </v-card-title>
      <div class="caption grey--text text--darken-1 px-4 pt-1 pb-3">{{ caption }}</div>
      <hr class="mb-3" />
      <v-card-text class="pa-0">
        <v-alert v-if="error" type="error" dense outlined>{{ error }}</v-alert>

        <!-- Nivel 1: contratos de la caja. -->
        <v-data-table
          v-else-if="showsContracts"
          :headers="contractHeaders"
          :items="contracts"
          :items-per-page="20"
          :footer-props="{ 'items-per-page-options': [20, 50, -1] }"
          :item-key="box === 'b2c' ? 'key' : 'contractCode'"
          no-data-text="No hay membresías activas"
          class="contracts-table"
          dense
          @click:row="(row) => $emit('select-contract', row)"
        >
          <template v-slot:item.contract="{ item }">
            <span class="pw-cell-link">{{ item.contractCode || 'Sin contrato' }}</span>
          </template>
          <template v-slot:item.name="{ item }">
            <span class="pw-cell-link">{{ item.name }}</span>
          </template>
          <template v-slot:item.modality="{ item }">{{ modalityLabel(item.modality) }}</template>
          <template v-slot:item.companyName="{ item }">{{ item.companyName || noContractHint(item) }}</template>
          <template v-slot:item.line="{ item }">{{ lineLabel(item.line) }}</template>
          <template v-slot:item.count="{ item }">{{ formatNumber(item.count) }}</template>
          <template v-slot:item.mrrUsd="{ item }">USD {{ formatNumber(item.mrrUsd) }}</template>
        </v-data-table>

        <!-- Nivel 2: membresías. -->
        <v-data-table
          v-else
          :headers="membershipHeaders"
          :items="rows"
          :loading="loading"
          :items-per-page="20"
          :footer-props="{ 'items-per-page-options': [20, 50, -1] }"
          no-data-text="No hay membresías activas"
          dense
        >
          <template v-slot:item.contract="{ item }">
            {{ item.contractCode || '—' }}<span v-if="item.companyName"> · {{ item.companyName }}</span>
          </template>
          <template v-slot:item.origin="{ item }">{{ originLabel(item) }}</template>
          <template v-slot:item.isBci="{ item }">{{ item.isBci ? 'Sí' : 'No' }}</template>
          <template v-slot:item.createdAt="{ item }">{{ displayDate(item.createdAt) }}</template>
          <!-- Comentario editable de Loyalty: Guardar emite `save-note`; Escape cancela. -->
          <template v-slot:item.comment="{ item }">
            <v-edit-dialog
              large
              save-text="Guardar"
              cancel-text="Cancelar"
              @open="draftNote = item.comment || ''"
              @save="$emit('save-note', { membershipId: item.membershipId, note: draftNote })"
            >
              <span :class="{ 'grey--text': !item.comment }">{{ item.comment || 'Agregar comentario' }}</span>
              <v-icon small class="ml-1">mdi-pencil-outline</v-icon>
              <template v-slot:input>
                <v-textarea
                  v-model="draftNote"
                  label="¿Por qué se entregó esta membresía?"
                  :counter="noteMaxLength"
                  :maxlength="noteMaxLength"
                  rows="3"
                  auto-grow
                  autofocus
                  class="mt-4"
                />
              </template>
            </v-edit-dialog>
          </template>
          <template v-slot:item.mrrUsd="{ item }">USD {{ formatNumber(item.mrrUsd) }}</template>
        </v-data-table>
      </v-card-text>
      <hr class="mt-3" />
      <v-card-actions class="justify-end">
        <v-btn text color="primary" @click="$emit('input', false)">Cerrar</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { formatNumber } from '@/utils/b2c-report.utils';

// Mismo límite que el backend (loyaltyNoteBodySchema).
const NOTE_MAX_LENGTH = 500;
import { CHANNEL_LABELS, SEGMENT_LABELS } from '@/utils/b2c-report-v2.labels';

const B2B2C_MODALITY_LABELS = {
  b2b2cDirectPay: 'Pago directo',
  b2b2cPayroll: 'Descuento por planilla'
};

const LINE_LABELS = { ...SEGMENT_LABELS, upsell: 'Upsell' };

const GROUP_MODALITY_LABELS = {
  direct: 'App y Felix',
  integrated: 'Broker integrado',
  dedicatedFelix: 'Felix dedicado',
  unclassified: 'Sin modalidad'
};

export default {
  name: 'B2CV2DetailDialog',
  props: {
    value: { type: Boolean, default: false },
    title: { type: String, default: '' },
    subtitle: { type: String, default: '' },
    // Nivel 1: [{ contractCode, companyName, count, mrrUsd }]. Vacío = la caja va directo al listado.
    contracts: { type: Array, default: () => [] },
    // Contrato abierto en el nivel 2, o null.
    contract: { type: Object, default: null },
    rows: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    error: { type: String, default: '' },
    // Muestra la columna Comentario (Loyalty), editable: emite `save-note` con
    // { membershipId, note }.
    showComment: { type: Boolean, default: false },
    // Caja abierta: decide si la lista de contratos muestra su línea o su modalidad de pago.
    box: { type: String, default: '' }
  },
  data() {
    return { draftNote: '', noteMaxLength: NOTE_MAX_LENGTH };
  },
  computed: {
    /**
     * B2B2C muestra si el convenio es pago directo o por planilla; el total y BCI, la línea
     * de cada contrato, porque mezclan varias. Upsell no la necesita: son todos contratos B2B.
     */
    lineHeader() {
      if (this.box === 'b2b2c') return { text: 'Modalidad', value: 'line', sortable: true };
      if (this.box === 'total' || this.box === 'bci') return { text: 'Línea', value: 'line', sortable: true };
      return null;
    },
    contractHeaders() {
      // B2C: Directo y cada broker, con su modalidad (en vez de contrato y empresa).
      if (this.box === 'b2c') {
        return [
          { text: 'Canal', value: 'name', sortable: false },
          { text: 'Modalidad', value: 'modality', sortable: false },
          { text: 'Contrato', value: 'contract', sortable: false },
          { text: 'Membresías', value: 'count', sortable: true, align: 'end' },
          { text: 'MRR', value: 'mrrUsd', sortable: true, align: 'end' }
        ];
      }
      return [
        { text: 'Contrato', value: 'contract', sortable: false },
        { text: 'Empresa', value: 'companyName', sortable: true },
        ...(this.lineHeader ? [this.lineHeader] : []),
        { text: 'Membresías', value: 'count', sortable: true, align: 'end' },
        { text: 'MRR', value: 'mrrUsd', sortable: true, align: 'end' }
      ];
    },
    showsContracts() {
      return this.contracts.length > 0 && !this.contract;
    },
    heading() {
      if (this.contract) {
        const name = this.box === 'b2c' ? this.contract.name : this.contract.contractCode || 'Sin contrato';
        return `${this.title} — ${name}`;
      }
      if (!this.showsContracts) return `Membresías activas — ${this.title}`;
      return this.box === 'b2c' ? `${this.title} por canal` : `${this.title} por contrato`;
    },
    caption() {
      if (this.showsContracts) {
        const total = this.contracts.reduce((sum, c) => sum + c.count, 0);
        const what = this.box === 'b2c' ? 'canales' : 'contratos';
        const one = this.box === 'b2c' ? 'un canal' : 'un contrato';
        return `${this.subtitle} · ${formatNumber(this.contracts.length)} ${what} · ${formatNumber(total)} membresías · haz clic en ${one} para ver sus membresías`;
      }
      const company = this.box !== 'b2c' && this.contract?.companyName ? `${this.contract.companyName} · ` : '';
      return `${company}${this.subtitle} · ${formatNumber(this.rows.length)} membresías`;
    },
    membershipHeaders() {
      return [
        { text: 'Titular', value: 'holderName', sortable: true },
        { text: 'Email', value: 'email', sortable: true },
        { text: 'Mascota', value: 'petName', sortable: true },
        { text: 'Plan', value: 'planName', sortable: true },
        { text: 'Contrato', value: 'contract', sortable: false },
        this.showComment
          ? { text: 'Comentario', value: 'comment', sortable: false }
          : { text: 'Origen', value: 'origin', sortable: false },
        { text: 'BCI', value: 'isBci', sortable: true },
        { text: 'Alta', value: 'createdAt', sortable: true },
        { text: 'MRR', value: 'mrrUsd', sortable: true, align: 'end' }
      ];
    }
  },
  methods: {
    formatNumber,
    displayDate(iso) {
      if (!iso) return '—';
      const [y, m, d] = iso.slice(0, 10).split('-');
      return `${d}/${m}/${y}`;
    },
    /** Segmento y canal ("B2C Directo · App"); el upsell y Loyalty no tienen segmento. */
    originLabel(item) {
      if (!item.segment) return '—';
      const segment = SEGMENT_LABELS[item.segment] || item.segment;
      const channel = item.brokerName || CHANNEL_LABELS[item.channel] || '';
      return channel ? `${segment} · ${channel}` : segment;
    },
    modalityLabel(modality) {
      return GROUP_MODALITY_LABELS[modality] || '—';
    },
    lineLabel(line) {
      if (this.box === 'b2b2c') return B2B2C_MODALITY_LABELS[line] || '—';
      return LINE_LABELS[line] || '—';
    },
    noContractHint(item) {
      return item.contractCode ? '—' : 'App o Felix, sin convenio';
    }
  }
};
</script>

<style scoped>
.contracts-table >>> tbody tr {
  cursor: pointer;
}
</style>
